import React from 'react';
import { useUser } from '@src/state/user/hooks';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Skeleton } from '@uhg-abyss/web/ui/Skeleton';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { Card } from '@uhg-abyss/web/ui/Card';
import { useForm as useAbyssForm } from '@uhg-abyss/web/hooks/useForm';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { Signature } from './Signature';

const Value = styled('div', {
  color: '$gray8',
  fontSize: '16px',
  lineHeight: '22px',
  fontWeight: 700,
  fontStyle: 'normal',
  marginTop: '2.3%',
  overflowWrap: 'break-word',
});

const SignatureValue = styled('div', {
  color: '$gray8',
  fontSize: '16px',
  lineHeight: '22px',
  fontWeight: 700,
  fontStyle: 'normal',
  marginTop: '5%',
  width: '200%',
  overflowWrap: 'break-word',
});

const Section = styled('div', {
  marginBottom: 10,
  '&:last-child': {
    marginBottom: 0,
  },
});

const Name = styled('div', {
  color: '$gray8',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '22px',
});

const BoldText = styled('div', {
  fontWeight: 'bold',
});

const CheckboxStyle = styled('div', {
  padding: '8px 0px',
});

export const AttestationData = () => {
  const form = useAbyssForm();
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const attestationData = form.getValues('attestation');
  const userInfo = useUser.state();

  const skeleton = (data, contentRenderer) => {
    return attestationData ? (
      contentRenderer(data)
    ) : (
      <Skeleton variant="rounded" height={20} width={200}>
        {contentRenderer('-')}
      </Skeleton>
    );
  };
  return (
    <Card header="Attestation">
      <Card.Section>
        <BoldText id="attestation-data-attestation-text">
          By signing this Provider Registration Form, I understand and agree to
          the following:
        </BoldText>
        <CheckboxStyle>
          <Checkbox id="attestation-data-checkbox-1" isChecked isDisabled>
            I attest that I am submitting as an individual and am the sole
            healthcare professional associated with this billing TIN OR I am
            submitting on behalf of my group/facility and I am authorized to
            submit registration information for this billing TIN.{' '}
          </Checkbox>
        </CheckboxStyle>
        <CheckboxStyle>
          <Checkbox id="attestation-data-checkbox-2" isChecked isDisabled>
            I agree to notify UnitedHealthcare in writing of any change in the
            information in this form within 10 business days of such change.
          </Checkbox>
        </CheckboxStyle>
        <CheckboxStyle>
          <Checkbox id="attestation-data-checkbox-3" isChecked isDisabled>
            I agree that the primary servicing address listed for the company is
            not a P.O. Box or a residential home but a building zoned for a
            business.
          </Checkbox>
        </CheckboxStyle>
        <CheckboxStyle>
          <Checkbox id="attestation-data-checkbox-4" isChecked isDisabled>
            Provider will not knowingly present or cause to be presented a false
            or fraudulent claim for payment to Plan, and I will not submit
            claims with deliberate ignorance or reckless disregard of their
            truth or falsity.
          </Checkbox>
        </CheckboxStyle>
        <CheckboxStyle>
          <Checkbox id="attestation-data-checkbox-5" isChecked isDisabled>
            None of the owners, practitioners, or providers or employees listed
            or that are employed by a provider is currently
            sanctioned,suspended, debarred, or excluded from participation in
            Medicare,Medicaid, or a federal health care program.
          </Checkbox>
        </CheckboxStyle>
        <CheckboxStyle>
          <Checkbox id="attestation-data-checkbox-6" isChecked isDisabled>
            <BoldText>
              I certify, represent and warrant that all information provided on
              this registration form is true, accurate and complete that I am
              authorized to act on behalf of the provider.
            </BoldText>
          </Checkbox>
        </CheckboxStyle>

        <Grid>
          <Grid.Col span="25%">
            <Section>
              <Name id="attestation-data-full-name">Full name</Name>
              <Value id="attestation-data-full-name-value">
                {skeleton(
                  attestationData && attestationData.submitterName,
                  (data) => {
                    return data || '-';
                  }
                )}
              </Value>
            </Section>
          </Grid.Col>
          <Grid.Col span="25%">
            <Section>
              <Name id="attestation-data-phone-number">Phone number</Name>
              <Value id="attestation-data-phone-number-value">
                {skeleton(
                  attestationData && attestationData.phoneNumber,
                  (data) => {
                    return data || '-';
                  }
                )}
              </Value>
            </Section>
          </Grid.Col>
          <Grid.Col span="30%">
            <Section>
              <Name id="attestation-data-preferred-contact">
                Preferred mode of contact
              </Name>
              <Value id="attestation-data-preferred-contact-value">
                {skeleton(
                  attestationData && attestationData.commPreference,
                  (data) => {
                    return data || '-';
                  }
                )}
              </Value>
            </Section>
          </Grid.Col>
          <Grid.Col span="20%">
            <Section>
              <Name id="attestation-data-preferred-header">
                Have you submitted a claim?
              </Name>
              <Value id="attestation-data-preferred-header-value">
                {skeleton(
                  attestationData && attestationData.submitClaim,
                  (data) => {
                    return data || '-';
                  }
                )}
              </Value>
            </Section>
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span="25%">
            <Section>
              <Name id="attestation-data-job-title">Job title</Name>
              <Value id="attestation-data-job-title-value">
                {skeleton(
                  attestationData && attestationData.jobTitle,
                  (data) => {
                    return data || '-';
                  }
                )}
              </Value>
            </Section>
          </Grid.Col>
          <Grid.Col span="30%">
            <Section>
              <Name id="attestation-data-email-address">Email address</Name>
              <Value id="attestation-data-email-address-value">
                {skeleton(userInfo && userInfo.email, (data) => {
                  return data;
                })}
              </Value>
            </Section>
          </Grid.Col>
          <Grid.Col span="30%">
            <Section>
              <Name id="attestation-data-signature">Signature</Name>
              <SignatureValue id="attestation-data-signature-value">
                <Signature />
              </SignatureValue>
            </Section>
          </Grid.Col>
          <Grid.Col span="20%">
            {attestationData && attestationData.submitClaim === 'Yes' ? (
              <Section>
                <Name id="attestation-data-signature">
                  Date the claim was submitted
                </Name>
                <Value id="attestation-data-preferred-contact-value">
                  {skeleton(
                    attestationData &&
                      attestationData.claimDate.replaceAll('/', '-'),
                    (data) => {
                      return data || '-';
                    }
                  )}
                </Value>
              </Section>
            ) : (
              featureToggles.getRow('US5661375_AARP')?.active && (
                <Section>
                  <Name id="attestation-data-health-program">
                    AARP Health Plan coverage
                  </Name>
                  <Value id="attestation-data-health-program">
                    {skeleton(
                      attestationData &&
                        (attestationData.healthProgram === 'aarp' ||
                          attestationData.healthProgram === 'uhc'),
                      (data) => {
                        return data ? 'Yes' : 'No';
                      }
                    )}
                  </Value>
                </Section>
              )
            )}
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span="25%">
            {attestationData &&
              attestationData.submitClaim === 'Yes' &&
              featureToggles.getRow('US5661375_AARP')?.active && (
                <Section>
                  <Name id="attestation-data-health-program">
                    AARP Health Plan coverage
                  </Name>
                  <Value id="attestation-data-health-program">
                    {skeleton(
                      attestationData &&
                        (attestationData.healthProgram === 'aarp' ||
                          attestationData.healthProgram === 'uhc'),
                      (data) => {
                        return data ? 'Yes' : 'No';
                      }
                    )}
                  </Value>
                </Section>
              )}
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};
